import Alpine from 'alpinejs';
import { createBoard } from './board.js';
import { createHome } from './home.js';
import { parsePeg, pegClasses, formatResult, explainResult, CODE_LENGTH } from './pegs.js';
import { mergeRows, rowMode } from './rows.js';
import { surge, cascade, wireTarget, buttonTarget } from './surge.js';
import { createCues } from './cues.js';
import { createHaptics } from './haptics.js';
import { createSounds } from './sounds.js';
import { createNotify, isApple } from './notify.js';
import { createWeekStrip } from './week.js';
import { createBackGuard } from './back.js';
import { createFreshen } from './stale.js';
import {
    ME, SOLO_GUESS, SOLO_FORFEIT, boardState, createSoloFetch, modeFromPath, modeLabel,
    ongoingGame, openGame, pickMode, reportTally, soloPath, startGame,
} from './solo.js';

// Exposed for debugging from the console.
window.unkoad = { parsePeg, pegClasses, formatResult, explainResult, mergeRows, rowMode, CODE_LENGTH };

// localStorage is not merely empty in a private window or with site data
// blocked - touching it throws. Probing once here keeps that in one place.
function safeStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

const storage = safeStorage();

// One of each channel for the page. Each remembers something the other cannot
// see - what the motor is already doing, whether the player has muted - and a
// second copy would hold a stale answer to it.
const sound = createSounds({ storage });
const cue = createCues([createHaptics(), sound]);

// What back means here. Installed before anything else touches history - the
// week strip pushes entries of its own, and the floor has to go under them.
// Every page gets one; what each page has that back should close is handed
// over below, by the component that owns it.
const back = createBackGuard();

// The daily's week strip, on the one page that has one. It steers panels the
// server already rendered, so it is wired up outside Alpine: there is no state
// here to bind, only which of seven is showing.
createWeekStrip();

/**
 * Fetches home's list back and swaps it in.
 *
 * The whole band structure, replaced outright rather than reconciled: a row
 * moving between bands is the ordinary case here - an opponent moving is
 * exactly a row crossing from "waiting on them" to "your move" - so there is
 * little in a list that a diff would have kept. Alpine initialises what
 * arrives, which is what keeps the mode bound on the forms inside it.
 */
async function refreshHomeList() {
    const list = document.querySelector('[data-home-list]');
    if (!list) return;

    try {
        const res = await fetch('/home/list', { headers: { Accept: 'text/html' } });
        if (!res.ok) return;

        const html = await res.text();
        // Re-read: the fetch is a round trip, and a list swapped in the
        // meantime is not the element this one started with.
        const target = document.querySelector('[data-home-list]');
        if (target) target.outerHTML = html;
    } catch (err) {
        // The page keeps the list it has, which is the list the server drew.
        console.warn('unkoad: could not refresh the list', err);
    }
}

/**
 * Fetches home's daily card back and swaps it in.
 *
 * The slot is swapped whole rather than its contents: an undealt day draws no
 * card, and that empty answer still has to replace the card on screen.
 */
async function refreshDailyCard() {
    if (!document.querySelector('[data-daily-slot]')) return;

    try {
        const res = await fetch('/home/daily', { headers: { Accept: 'text/html' } });
        if (!res.ok) return;

        const html = await res.text();
        // Re-read: the fetch is a round trip, and a slot swapped in the
        // meantime is not the one this call started with.
        const target = document.querySelector('[data-daily-slot]');
        if (target) target.outerHTML = html;
    } catch (err) {
        // The page keeps the card it has, which is the card the server drew.
        console.warn('unkoad: could not refresh the daily card', err);
    }
}

/**
 * Refreshes the daily card when home comes back rather than loads.
 *
 * A phone does not reload the page it returns to: back is served from the
 * back-forward cache and a backgrounded tab is frozen where it stands, so home
 * can sit off screen across the release hour. pageshow covers the history
 * restore, visibilitychange the frozen tab.
 *
 * Only the card is re-read; the list has a listener of its own.
 */
function watchHomeReturn() {
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) refreshDailyCard();
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refreshDailyCard();
    });
}

/**
 * Home, live.
 *
 * Imported here rather than at the top of the file for the same reason the
 * board defers it: the Firestore SDK is three quarters of the bundle, and home
 * is the page every visit passes through. The list is on the screen and every
 * row on it works before this resolves.
 */
async function goHomeLive(main) {
    const uid = main.dataset.uid;
    if (!uid || !window.__unkoadFirebase) return;

    try {
        const { goLive } = await import('./live.js');
        const detach = await goLive({
            config: window.__unkoadFirebase,
            uid,
            onChange: refreshHomeList,
        });
        if (detach) window.addEventListener('pagehide', detach, { once: true });
    } catch (err) {
        console.warn('unkoad: live updates unavailable', err);
    }
}

/**
 * What a last-seen home holds until it has been fetched back: everything that
 * needs the server. Solo, the mode and the sound are the phone's own. The
 * stylesheet holds the same set from the first paint; this is what takes it
 * out of the keyboard's reach as well.
 */
const HELD = '[data-edit="name"], [data-notify], [data-daily-slot], [data-home-list] > :not([data-solo], .band)';

const STALE_NOTES = {
    updating: 'Updating\u2026',
    offline: 'No connection. Solo still works.',
};

/**
 * Fetches home and puts what it says into the page that is already drawn.
 *
 * The whole page in one request, rather than the list and the card in two:
 * it is the request the worker keeps as the next visit's last-seen home.
 */
async function loadFreshHome(main) {
    const res = await fetch('/', { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`home answered ${res.status}`);

    const page = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = page.querySelector('[data-home]');
    if (!fresh) throw new Error('home came back without a home');

    // The worker also draws this page for an address it could not reach, and
    // a cleared cookie makes it somebody else's home. Either way the server
    // can now answer for itself.
    if (window.location.pathname !== '/' || fresh.dataset.uid !== main.dataset.uid) {
        window.location.reload();
        return;
    }

    for (const part of ['[data-home-list]', '[data-daily-slot]']) {
        const from = fresh.querySelector(part);
        const to = main.querySelector(part);
        if (from && to) to.outerHTML = from.outerHTML;
    }

    const name = fresh.querySelector('[data-edit="name"]')?.textContent.trim();
    const state = Alpine.$data(main);
    if (name && state.name === state.savedName) {
        state.name = name;
        state.savedName = name;
    }
}

/** Holds a last-seen home until it is fresh, then lets it go live. */
function freshenHome(main) {
    const root = document.documentElement;
    const note = main.querySelector('[data-stale-note]');
    const hold = (held) => main.querySelectorAll(HELD).forEach((el) => { el.inert = held; });

    hold(true);
    const freshen = createFreshen({
        load: () => loadFreshHome(main),
        onState(state) {
            if (note && STALE_NOTES[state]) note.textContent = STALE_NOTES[state];
            if (state !== 'fresh') return;
            delete root.dataset.stale;
            hold(false);
            goHomeLive(main);
        },
    });

    window.addEventListener('online', freshen.retry);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') freshen.retry();
    });
    freshen.start();
}

const homeMain = document.querySelector('[data-home]');
if (homeMain) {
    watchHomeReturn();
    if ('stale' in document.documentElement.dataset) {
        // Once Alpine has drawn the page: the fresh name is written into its state.
        document.addEventListener('alpine:initialized', () => freshenHome(homeMain), { once: true });
    } else {
        goHomeLive(homeMain);
    }
}

Alpine.data('home', (options = {}) => {
    const home = createHome({
        ...options,
        storage,
        cue,
        sound,
        notify: createNotify({ publicKey: options.vapidKey || '' }),
    });

    // What home has that back should close, rather than leave the page for: the
    // sheets behind the three words under the wordmark, and the panel a long
    // press on a game opens to ask whether it is really being given up. Only
    // one of them is ever up, so back has only ever one thing to shut.
    //
    // Watched rather than called from each way in and out, because there are
    // eight of those - Escape, the scrim, choosing a mode, committing a name -
    // and a single one of them forgotten is a back button that closes a sheet
    // that is not there.
    const opened = () => home.open !== '' || home.forfeiting !== '';
    const homeInit = home.init;
    home.init = function init() {
        homeInit.call(this);
        // Back is a tap away rather than an Escape: it puts a half-typed name
        // away with the sheet, the way the scrim does, instead of taking it.
        const shut = () => {
            this.forfeiting = '';
            this.close();
        };
        const tell = () => back.sync(opened(), shut);
        this.$watch('open', tell);
        this.$watch('forfeiting', tell);
    };

    return home;
});

/**
 * Registers the service worker on every visit, not only when pings are turned
 * on: it is what serves solo when there is no connection.
 */
function registerWorker() {
    const worker = navigator.serviceWorker;
    if (!worker) return;
    worker.register('/sw.js').catch((err) => console.warn('unkoad: no service worker', err));
}

registerWorker();

// Solo play is counted on the phone and sent whenever there is a connection to
// send it over: on every page load, and when the browser says one came back.
reportTally(storage);
window.addEventListener('online', () => reportTally(storage));

/**
 * Leaves for a solo board. Assigned rather than linked, so tests can stub it.
 *
 * A sheet shut on the way out hands its history entry back, and that has to
 * land first or it takes the board back with it. The pause before asking is
 * for Alpine, which tells the guard about the sheet a tick after it shut.
 */
async function goSolo(mode) {
    await new Promise((resolve) => { setTimeout(resolve); });
    await back.settled();
    window.location.assign(soloPath(mode));
}

/**
 * The solo line on home: the game on the phone to
 * continue, or the offer to deal one.
 *
 * Drawn here rather than by the server, which never sees a solo game. Read on
 * init, which Alpine runs again whenever home swaps its list in.
 */
// The kept game's mode is keptMode rather than mode: on home this component
// sits inside home's, whose mode is the picker's, and a name here would hide it.
Alpine.data('soloLine', () => ({
    ongoing: false,
    keptMode: '',
    rows: 0,

    init() {
        const game = ongoingGame(storage);
        this.ongoing = Boolean(game);
        this.keptMode = game ? game.mode : '';
        this.rows = game ? game.rows.length : 0;
    },

    get label() {
        return modeLabel(this.keptMode);
    },

    get rowsLabel() {
        return `${this.rows} row${this.rows === 1 ? '' : 's'}`;
    },

    /** Deals a game in the picker's mode, or carries on with the one kept. */
    start(value) {
        if (this.ongoing) {
            goSolo(this.keptMode);
            return;
        }
        goSolo(startGame(storage, pickMode(value)).mode);
    },
}));

/**
 * A solo board: the game board, answered by solo.js instead of the server.
 *
 * The page is the same for every game in a mode, cached by the service worker,
 * so the game itself comes out of storage here rather than off the page.
 */
Alpine.data('soloBoard', (options = {}) => {
    const mode = modeFromPath(window.location.pathname);
    const game = openGame(storage, mode || 'Four');
    const state = boardState(game);

    const board = boardComponent({
        ...options,
        me: ME,
        rows: state.rows,
        turn: state.turn,
        gameOver: state.game_over,
        won: state.won,
        solo: true,
        guessUrl: SOLO_GUESS,
        forfeitUrl: SOLO_FORFEIT,
        draftKey: `solo:${game.id}`,
        fetchImpl: createSoloFetch(storage),
    });

    board.modeLabel = modeLabel(game.mode);

    // There is no stored game to listen to, and the board must not ask the
    // server for anything: it may be playing without a connection.
    board.attachLive = async function attachLive() {};

    /** Deals the next game in the same kind of mode. */
    board.playAgain = function playAgain() {
        goSolo(startGame(storage, pickMode(parseKind(game.mode))).mode);
    };

    return board;
});

function parseKind(mode) {
    return mode.split('|')[0];
}

Alpine.data('board', (options = {}) => boardComponent(options));

function boardComponent(options = {}) {
    // Properties are added to the board rather than spread into a new object,
    // because spreading would evaluate its getters (myTurn, ready) once and
    // freeze them as plain values.
    // The board decides when something surges; this is the only place that
    // knows what a surge lands on, which is what keeps board.js free of the DOM.
    const board = createBoard({
        ...options,
        // Where the board remembers that this player has already been told what
        // a score means, so it only ever tells them once.
        storage,
        cue,
        // One pattern across both, because the wire and the button are one
        // circuit closing - two rolls would disagree about when current came.
        onSurge() {
            surge('guess', [
                wireTarget(this.$refs.guessWire),
                buttonTarget(this.$refs.guessButton),
            ]);
        },
        // The rows are a scroll box of their own - the header above them and
        // the guess area below are pinned to the screen - and a scroll box
        // opens at its top, which on a long game is the oldest row. The board
        // is built the other way round: the newest row is the one the palette
        // underneath it is about to answer, so it is the end that is kept in
        // view. A tick, because the row that caused this arrived in the same
        // update and is not on the page yet.
        onNewRow() {
            this.$nextTick(() => {
                const list = this.$refs.rowList;
                if (list) list.scrollTop = list.scrollHeight;
            });
        },
        // An explanation opens under its row, which on the bottom rows of a full
        // list is below the edge of the scroll box. The list moves only as far
        // as the explanation needs, and never so far that the row it explains
        // leaves the top on a screen too short to hold both.
        onExplain(line) {
            this.$nextTick(() => {
                const list = this.$refs.rowList;
                const group = list && list.querySelector(`[data-group][data-line="${line}"]`);
                if (!group) return;

                const box = list.getBoundingClientRect();
                const { top, bottom } = group.getBoundingClientRect();
                const gutter = parseFloat(getComputedStyle(list).paddingBottom) || 0;
                const hidden = bottom + gutter - box.bottom;
                if (hidden > 0) list.scrollTop += Math.min(hidden, top - box.top);
            });
        },
        // The wave runs from the row that won it back up through the game, so
        // it starts where the player is already looking. It waits a tick
        // because the winning row is part of the same update and is not on the
        // page yet; without that it would celebrate every row but the one.
        onCelebrate() {
            this.$nextTick(() => {
                const wires = [...this.$refs.rowList.querySelectorAll('[data-row] .peg-wire')];
                cascade('win', wires.reverse().map(wireTarget));
            });
        },
    });

    /** A board that loads part-way through a game opens on its newest row. */
    board.init = function init() {
        this.onNewRow();
    };

    /**
     * Strikes a row's wire as it lands. Called from x-init, which Alpine runs
     * once per element it creates - so unlike the old :class binding this
     * cannot re-fire on an unrelated board change, and the pattern no longer
     * has to be seeded from the row to stay put.
     */
    board.strikeRow = function strikeRow(el, row) {
        if (!this.isFreshRow(row)) return;
        surge('row', [wireTarget(el)]);
    };

    // Owns both the snapshot subscription and the page-lifecycle handlers that
    // revive it, so there is one thing to call on the way out.
    board.detach = null;

    // Whether this board has left the page. Read by attachLive, which is
    // awaiting a chunk over the network and can land after the player has gone.
    board.destroyed = false;

    /*
     * The offers to turn notifications on. The board only ever offers - it
     * shows a line at the moments a player is waiting on somebody else - and
     * the enable flow behind the line is the same one the home page bell runs.
     * Turning them off lives on the home page alone.
     */
    const notify = createNotify({ publicKey: options.vapidKey || '' });

    board.notifyState = 'unsupported';
    board.installHelp = false;

    // Whether the flow is running, which is what the bell's box reports while
    // it is. It also holds the offer shut, so a second tap on a card that has
    // not answered yet cannot file a second subscription.
    board.notifyBusy = false;

    // Whether the last attempt reached the server and got nowhere. Not a
    // notifyState, because nothing was kept: the browser really is off, and
    // the offer really is still worth making.
    board.notifyFailed = false;

    // Which way round this platform draws share and copy. The same answer that
    // decides whether push needs a home-screen install first, so the two can
    // never disagree about what kind of machine this is.
    board.apple = isApple(window);

    /** Whether an offer is worth making: nothing on, nothing refused. */
    board.notifyOffered = function notifyOffered() {
        return this.notifyState === 'off' || this.notifyState === 'needs-install';
    };

    /**
     * Whether this is the moment the board offers to watch the turn: somebody
     * else is thinking, and this tab is about to be left behind. Whether that
     * offer is a card or a footnote is notifyLoud's answer; where it sits is
     * the same place either way.
     */
    board.notifyTurnOffer = function notifyTurnOffer() {
        return !this.myTurn && !this.awaitingOpponent && this.notifyOffered();
    };

    board.enableNotify = async function enableNotify() {
        if (this.notifyState === 'needs-install') {
            this.installHelp = !this.installHelp;
            return;
        }
        if (this.notifyBusy) return;

        this.notifyFailed = false;
        this.notifyBusy = true;
        try {
            const result = await notify.enable();
            if (result === 'needs-install') {
                this.installHelp = true;
                return;
            }
            this.notifyFailed = result === 'failed';
            this.notifyState = result === 'failed' ? 'off' : result;
        } finally {
            this.notifyBusy = false;
        }
    };

    board.refreshNotify = async function refreshNotify() {
        this.notifyState = await notify.status();
        // A subscription the server lost heals on the next board visit.
        if (this.notifyState === 'on') notify.resync();
    };

    board.init = function init() {
        this.refreshNotify();
        this.watchClock();
        // The one thing a board opens on top of itself. Back takes the wire
        // back, which is what every other way out of it does - see toggleSay.
        this.$watch('saying', (saying) => back.sync(saying, () => this.closeSay()));
        // Live updates are an enhancement. With no Firebase config the board
        // runs on POST responses alone, which is also how Playwright drives it.
        if (!window.__unkoadFirebase) return;
        this.attachLive();
    };

    // Stops the clock following the page about, so there is one thing to call
    // on the way out.
    board.clockOff = null;

    /**
     * Hands the daily's clock the one thing board.js cannot know: whether the
     * day is actually in front of the player.
     *
     * Visibility is the whole test. Another tab, another app, a locked phone
     * and a closed window all arrive here as hidden, and all of them mean the
     * same thing - nobody is solving. A window merely behind another one does
     * not: it is still on the screen, and it is still being read.
     *
     * pagehide is the desktop close, which does not always bother with a
     * visibility change on the way out; pageshow is the bfcache restore coming
     * back, which does not always bother either.
     */
    board.watchClock = function watchClock() {
        if (!options.daily || this.gameOver) return;

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') this.pauseClock();
            else this.resumeClock();
        };
        const onHide = () => this.pauseClock();
        const onShow = () => this.resumeClock();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onHide);
        window.addEventListener('pageshow', onShow);

        this.clockOff = () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onHide);
            window.removeEventListener('pageshow', onShow);
            this.pauseClock();
        };

        // A board restored into a background tab must not count the time it
        // spends there, so the clock is started by asking rather than assumed.
        onVisibility();
    };

    /**
     * Fetches the Firestore SDK and subscribes to this game.
     *
     * Imported here rather than at the top of the file because this is the
     * only thing in the app that needs it, and it is three quarters of the
     * bundle by weight: as a static import every page paid for it, including
     * the ones with no game on them. The board is interactive before this
     * resolves, and a board whose listener never attaches is the same board
     * that runs where the config is absent - it learns of a move on the next
     * response instead of live.
     */
    board.attachLive = async function attachLive() {
        try {
            const { attachListener, signIn } = await import('./listener.js');
            // A game is readable by the players in it and nobody else, so the
            // board has to say who it is before it can watch its own game.
            // Without a credential there is simply no listener, which is the
            // board that has always been served where live updates are off.
            if (!(await signIn(window.__unkoadFirebase, this.me))) return;
            // Awaiting is a gap the player can leave in: a subscription opened
            // after the board has gone would have nothing left to call it off.
            if (this.destroyed) return;
            this.detach = attachListener(this, window.__unkoadFirebase);
        } catch (err) {
            console.warn('unkoad: live updates unavailable', err);
        }
    };

    board.destroy = function destroy() {
        this.destroyed = true;
        if (typeof this.detach === 'function') this.detach();
        this.detach = null;
        if (typeof this.clockOff === 'function') this.clockOff();
        this.clockOff = null;
    };

    return board;
}

window.Alpine = Alpine;
Alpine.start();
